import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

// import Search from "@/components/Sidebar/Search";
import Navigation from '@/components/Sidebar/Navigation';
import { Team, TeamInitials, Teams } from '@/components/Sidebar/Teams';
import Text from '@/components/Text';
import useIsGeneralMode from '@/hooks/useIsGeneralMode';
import { RootState } from '@/utils/store';

import ChannelsUpdateNotification from '../Notifications/ChannelsUpdateNotification';

const Wrap = styled.aside`
  align-content: flex-start;
  width: 264px;
  background: ${({ theme }) => theme.colors.blue};
  display: flex;
  overflow-y: auto;
  flex-wrap: wrap;
  -webkit-overflow-scrolling: touch;
  ${({ theme }) => theme.breakpoints.mediumDesktop} {
    width: 56px;
  }
`;

const Content = styled.div<{ $isExpanded?: boolean }>`
  flex: 1;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  position: relative;
  display: flex;
  flex-direction: column;
  padding-bottom: 30px;
  justify-content: space-between;
  ${({ theme }) => theme.breakpoints.mediumDesktop} {
    position: absolute;
    left: -208px;
    transition: left ease-out 0.2s;
    height: calc(100vh - 73px);
    overflow-y: auto;
    width: 208px;
    z-index: 100;
    background: ${({ theme }) => theme.colors.blue};
    ${({ $isExpanded }) =>
      $isExpanded &&
      css`
        left: 56px;
      `};
  }
`;

const Header = styled.div<{ $isExpanded?: boolean }>`
  width: 100%;
  justify-content: center;
  display: flex;
  flex-direction: column;
  padding: 8px 16px;
  min-height: 80px;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  ${({ theme }) => theme.breakpoints.mediumDesktop} {
    position: absolute;
    left: -208px;
    transition: left ease-out 0.2s;
    height: 73px;
    min-height: auto;
    background: ${({ theme }) => theme.colors.blue};
    z-index: 100;
    width: 208px;
    ${({ $isExpanded }) =>
      $isExpanded &&
      css`
        left: 56px;
      `};
  }
`;

const HeaderHolder = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`;

const OrganisationName = styled(Text)`
  opacity: 0.5;
  text-transform: uppercase;
`;

const TeamName = styled(Text)`
  line-height: 18px;
`;

const Inner = styled.div`
  padding: 4px 16px 24px;
`;

const ContentHolder = styled.div`
  width: 100%;
  width: 264px;
  display: flex;
  min-height: calc(100% - 81px);
`;

const TeamHolder = styled.div`
  width: 56px;
  padding: 20px 8px;
  display: grid;
  grid-template-columns: 32px;
  grid-row-gap: 16px;
  justify-content: center;
  flex-shrink: 0;
  height: 100%;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  ${({ theme }) => theme.breakpoints.mediumDesktop} {
    z-index: 101;
    background: ${({ theme }) => theme.colors.blue};
  }
`;

const FooterText = styled(Text)`
  width: 100%;
  padding-bottom: 30px;
  text-align: center;
  a {
    text-decoration: underline;
  }
`;

const Sidebar: FC = () => {
  const isGeneralMode = useIsGeneralMode();

  const { teamId } = useParams();

  const [isExpanded, setIsExpanded] = useState(false);

  const organisationName = useSelector(
    (state: RootState) => state.auth.user!.organisation!.name,
  );

  const teamTitle = useSelector(
    (state: RootState) => state.teams[teamId || '']?.name,
  );

  const title = teamId ? (teamId === 'all' ? 'God Mode' : teamTitle) : 'Home';

  function getCookie(name: string) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts?.pop()?.split(';').shift();
  }

  return (
    <Wrap
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      <HeaderHolder>
        <TeamHolder>
          <Team
            to="/general"
            className={`${isGeneralMode ? 'active' : ''} home-mode`}
            data-tip="Home"
            data-for="sidebar-teams-tooltip"
          >
            <TeamInitials as="span">
              <svg
                width="20"
                height="21"
                viewBox="0 0 20 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M9.99874 0.0803833C9.54988 0.0803985 9.1189 0.24958 8.78964 0.552114L0.873514 7.82559C0.689232 7.99491 0.542658 8.20065 0.442592 8.42927C0.342536 8.65786 0.291016 8.90463 0.291016 9.15386V18.4C0.291016 18.874 0.477263 19.3307 0.81209 19.6691C1.14727 20.0078 1.60411 20.2 2.08268 20.2H17.916C18.3946 20.2 18.8514 20.0078 19.1866 19.6691C19.5214 19.3307 19.7077 18.874 19.7077 18.4V9.15389C19.7077 8.90464 19.6562 8.65785 19.5561 8.42925C19.456 8.20062 19.3094 7.99487 19.1251 7.82554L11.2079 0.552033C10.8786 0.24952 10.4476 0.0803682 9.99874 0.0803833ZM9.99881 2.15714L17.7077 9.23925V18.2H14V12C14 11.4477 13.5523 11 13 11H7C6.44772 11 6 11.4477 6 12V18.2H2.29102V9.23921L9.99881 2.15714ZM8 18.2H12V13H8V18.2ZM9.64254 1.48037L9.9988 1.86812L10.1426 2.02508L9.64254 1.48037Z"
                  fill="#ffffff"
                />
              </svg>
            </TeamInitials>
          </Team>
        </TeamHolder>
        <Header $isExpanded={isExpanded}>
          <OrganisationName as="h1" $type="extraSmall" $color="white">
            {organisationName}
          </OrganisationName>
          <TeamName as="h2" $type="caption" $color="white">
            {title}
          </TeamName>
        </Header>
      </HeaderHolder>

      <ContentHolder>
        <Teams />
        <Content $isExpanded={isExpanded}>
          <div>
            <Inner>
              {/* TODO: uncomment when needed */}
              {/* <Search /> */}
              <Navigation />
            </Inner>
          </div>

          <FooterText $type="extraSmall" $color="white" $opacity={0.2}>
            © 2022 Backbone |{' '}
            <a
              href="https://surveys.hotjar.com/13dd9598-77c9-4013-90ec-ce073873adcd"
              target="_blank"
              rel="noreferrer noopener"
            >
              Share Feedback
            </a>
          </FooterText>
        </Content>
      </ContentHolder>
      {!getCookie('channelsAlertsBiscuit4331531') && (
        <ChannelsUpdateNotification />
      )}
    </Wrap>
  );
};

export default Sidebar;
